import React from "react";
import { motion } from "framer-motion";
import { VENUE } from "../lib/venue";
import { MapPin, Clock } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1575444758702-4a6b9222336e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxsaXZlJTIwbXVzaWMlMjBjcm93ZCUyMGJhcnxlbnwwfHx8fDE3NzkwOTk5NTJ8MA&ixlib=rb-4.1.0&q=85"
          alt="Inside The Bar Colac — Colac pub interior with live music vibe"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_#0A0A0A_85%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 pt-32 pb-20 w-full">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#F5A623]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#F5A623] font-semibold">
                Colac's Loudest Living Room
              </span>
            </div>

            <h1 className="sr-only">
              The Bar Colac — Live Music, Karaoke & Private Hire in Colac, Victoria
            </h1>

            <div
              data-testid="hero-title"
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-[#FDFBF7] uppercase leading-[0.85] whitespace-nowrap"
            >
              Great Drinks.
              <br />
              <span className="text-[#F5A623]">Great Times.</span>
              <br />
              Great People.
            </div>

            <p className="mt-8 max-w-xl text-lg text-[#FDFBF7]/70 leading-relaxed">
              Colac's home of live entertainment every weekend — karaoke, live
              music and a room you can take over for your next big night out.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#events"
                data-testid="hero-cta-events"
                className="inline-flex items-center bg-[#F5A623] hover:bg-amber-400 text-black font-bold uppercase tracking-wider px-8 py-4 border-2 border-[#F5A623] transition-all shadow-[4px_4px_0px_0px_rgba(245,166,35,0.4)] hover:shadow-[6px_6px_0px_0px_rgba(245,166,35,0.6)] hover:-translate-y-0.5"
              >
                Upcoming Events
              </a>
              <a
                href="#hire"
                data-testid="hero-cta-hire"
                className="inline-flex items-center bg-transparent hover:bg-white/5 text-[#FDFBF7] border-2 border-white/25 font-bold uppercase tracking-wider px-8 py-4 transition-all"
              >
                Private Hire
              </a>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6 text-sm text-[#FDFBF7]/65">
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-[#F5A623]" />
                <span>27 Murray Street, Colac VIC</span>
              </div>
              <div className="hidden sm:block h-4 w-px bg-white/15" />
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-[#F5A623]" />
                <span>Fri & Sat · 8pm – Late</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:flex justify-center"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-[#F5A623]/20 blur-3xl rounded-full" />
              <img
                src={VENUE.logoUrl}
                alt="The Bar Colac logo"
                className="relative w-[400px] h-[400px] xl:w-[460px] xl:h-[460px] object-contain drop-shadow-[0_0_40px_rgba(245,166,35,0.35)]"
                data-testid="hero-logo"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#FDFBF7]/50">
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-[#F5A623] to-transparent" />
      </div>
    </section>
  );
};
